import { dayjs } from "@lib";
import {
	API_ORIGIN_TO_DOMAIN_ORIGIN,
	API_STATUS_TO_INFRACTION_STATUS,
	DISPLAY_STATUS_TO_INFRACTION_STATUS,
	INFRACTION_DISPLAY_STATUS,
	INFRACTION_OFFENDER_TYPE,
} from "../constants/infraction-api-enums.constant";
import {
	INFRACTION_SEVERITY,
	INFRACTION_STATUS,
} from "../constants/infraction-status.constant";
import type {
	InfractionDetail,
	InfractionHistoryItem,
	InfractionSeverity,
} from "../types/infraction.types";
import type { InfractionDetailApiResponse } from "../types/infraction-api.types";

const API_SEVERITY_TO_DOMAIN: Record<number, InfractionSeverity> = {
	1: INFRACTION_SEVERITY.LIGHT,
	2: INFRACTION_SEVERITY.MEDIUM,
	3: INFRACTION_SEVERITY.SEVERE,
	4: INFRACTION_SEVERITY.VERY_SEVERE,
};

function buildVehicleLabel(brand: string | null, model: string | null): string {
	const parts = [brand, model].filter(Boolean);
	if (parts.length === 0) {
		return "—";
	}

	return parts.join(" ");
}

function formatOccurredTime(isoDate: string): string {
	const timePart = isoDate.split("T")[1];
	if (!timePart) {
		return "—";
	}

	const [hour, minute] = timePart.split(":");
	if (!(hour && minute)) {
		return "—";
	}

	return `${hour}:${minute}`;
}

function isNicInfraction(detail: InfractionDetailApiResponse): boolean {
	const breakdown = detail.breakdown?.toLowerCase() ?? "";
	const description = detail.description?.toLowerCase() ?? "";
	return breakdown.includes("nic") || description.includes("nic");
}

function buildSeverityTypeLabel(
	severityLabel: string | null,
	points: number | null
): string {
	if (!severityLabel) {
		return "—";
	}

	if (points !== null && points !== undefined) {
		return `${severityLabel} - ${points} pts`;
	}

	return severityLabel;
}

function buildDeadlineAlert(detail: InfractionDetailApiResponse): {
	title: string;
	subtitle: string;
} | null {
	if (!detail.driverIndicationDeadline) {
		return null;
	}

	if (
		detail.displayStatus !==
		INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION
	) {
		return null;
	}

	const deadline = dayjs(detail.driverIndicationDeadline).startOf("day");
	const today = dayjs().startOf("day");
	const daysRemaining = deadline.diff(today, "day");

	if (daysRemaining < 0 || daysRemaining > 15) {
		return null;
	}

	const formattedDeadline = deadline.format("DD/MM/YYYY");
	const daysLabel =
		daysRemaining === 0
			? "Vence hoje"
			: `Faltam ${daysRemaining} ${daysRemaining === 1 ? "dia" : "dias"}`;

	return {
		title: `${daysLabel} para o prazo limite de indicação (${formattedDeadline}).`,
		subtitle: "Risco de Multa NIC (Valor dobrado) em caso de não indicação.",
	};
}

function mapHistory(
	history: InfractionDetailApiResponse["history"]
): InfractionHistoryItem[] {
	return (history ?? []).map((item) => ({
		type: item.type,
		typeLabel: item.typeLabel,
		actorType: item.actorType,
		actorTypeLabel: item.actorTypeLabel,
		actorName: item.actorName,
		occurredAt: item.occurredAt,
		referenceDate: item.referenceDate,
		driverName: item.driverName,
	}));
}

function mapDetailListFields(detail: InfractionDetailApiResponse) {
	const status =
		DISPLAY_STATUS_TO_INFRACTION_STATUS[detail.displayStatus] ??
		API_STATUS_TO_INFRACTION_STATUS[detail.status] ??
		INFRACTION_STATUS.NOTIFICATION_RECEIVED;
	const origin = API_ORIGIN_TO_DOMAIN_ORIGIN[detail.origin] ?? "integrated";

	return {
		id: String(detail.id),
		plate: detail.plate ?? "—",
		vehicleLabel: buildVehicleLabel(detail.vehicleBrand, detail.vehicleModel),
		autoNumber: detail.ait ?? "—",
		infractionCode: detail.infractionCode,
		breakdown: detail.breakdown,
		description: detail.description ?? "—",
		driverName: detail.driverName,
		status,
		statusCode: detail.status,
		displayStatus: detail.displayStatus,
		statusLabel: detail.displayStatusLabel ?? detail.statusLabel ?? status,
		origin,
		originLabel: detail.originLabel,
		isAntt: detail.isAntt || detail.issuingAuthorityIsAntt,
		isNic: isNicInfraction(detail),
		deadlinePrimaryText: "—",
		deadlineDateShort: "",
		deadlineUrgency: "normal" as const,
		amount: detail.originalAmount,
		isDriverResponsible:
			detail.offenderType === INFRACTION_OFFENDER_TYPE.DRIVER,
		hasPaymentGuide: false,
		occurredAt: detail.occurredAt,
		issuingAuthorityCode: detail.issuingAuthorityCode,
		issuingAuthorityDescription: detail.issuingAuthorityDescription,
		driverIndicationDeadline: detail.driverIndicationDeadline,
		dueDate: detail.dueDate,
		paymentDate: detail.paymentDate,
		offenderTypeLabel: detail.offenderTypeLabel,
	};
}

export function mapInfractionDetail(
	detail: InfractionDetailApiResponse
): InfractionDetail {
	const severity =
		API_SEVERITY_TO_DOMAIN[detail.severity] ?? INFRACTION_SEVERITY.MEDIUM;
	const deadlineAlert = buildDeadlineAlert(detail);

	return {
		...mapDetailListFields(detail),
		issuerCode: detail.issuingAuthorityCode ?? "—",
		issuerName: detail.issuingAuthorityDescription ?? "—",
		occurredTime: formatOccurredTime(detail.occurredAt),
		location: detail.location ?? "—",
		infractionTypeCode: detail.ait ?? "—",
		severity,
		severityLabel: detail.severityLabel,
		severityTypeLabel: buildSeverityTypeLabel(
			detail.severityLabel,
			detail.points
		),
		points: detail.points,
		indicationDeadline: detail.driverIndicationDeadline ?? "",
		paymentDeadline: detail.dueDate ?? "",
		attachments: (detail.attachments ?? [])
			.filter((attachment) => attachment.hasFile)
			.map((attachment) => ({
				id: String(attachment.id),
				type: attachment.type,
				typeLabel: attachment.typeLabel,
				fileName: attachment.fileName ?? "Anexo",
				fileSizeBytes: attachment.fileSize,
				mimeType: "",
			})),
		hasNicRisk: isNicInfraction(detail),
		isCriticalDeadline: deadlineAlert !== null,
		deadlineAlertTitle: deadlineAlert?.title ?? null,
		deadlineAlertSubtitle: deadlineAlert?.subtitle ?? null,
		canEdit: detail.canEdit,
		driverIndicationAvailable: detail.driverIndicationAvailable,
		history: mapHistory(detail.history),
	};
}
